import Ad from "@components/Advertisement/Ad";
import Container from "@components/Container/Container";
import SeoFooter from "@components/Footer/SeoFooter/SeoFooter";
import { GADataProps } from "@components/GAData/GAData";
import Header from "@components/Header/Header";
import { useBreadCrumbState } from "@components/Header/helpers";
import PulseEntry from "@features/pulse/components/PulseEntry/PulseEntry";
import { getBreadcrumbProperties } from "@features/seo/helpers/getJsonLdProperties";
import { useInjectClientData } from "@hooks/useInjectClientData";
import { CardVariations } from "@particles/card/utils/types";
import Listing from "@particles/listing/Listing";
import ThriveNavigation from "@routes/Section/layouts/Thrive/components/ThriveNavigation";
import ThriveNewsletter from "@routes/Section/layouts/Thrive/components/ThriveNewsLetter";
import ThriveSocialMediaBanner from "@routes/Section/layouts/Thrive/components/ThriveSocialMediaBanner";
import ThriveTopBanner from "@routes/Section/layouts/Thrive/components/ThriveTopBanner";
import { ThrivePageData } from "@routes/Section/utils/types";
import { USFBreadcrumbSchema } from "@sphtech/web2-seo-schema";
import { sectionNavigationItems } from "@util/constant";

/**
 * Component to display Thrive page
 * https://www.figma.com/design/UWKZEzXYf0Gf5e6sfqsKVC/THRIVE-WORKING-FILE?node-id=8869-84185&m=dev
 */
const ThrivePage = ({
  entity,
  articles,
  seoFooterData,
  gaProps,
}: ThrivePageData & { gaProps: GADataProps }): React.ReactElement => {
  const { urlPath, uniqueName } = entity;
  const { breadcrumbsRef, isShowBreadCrumb } = useBreadCrumbState();

  const templateHVariant: CardVariations = {
    image: {
      width: "w-full lg:w-1/2",
      position: "left",
    },
    content: {
      width: "w-full lg:w-1/2",
      extraClass:
        "h-full p-4 lg:pt-0 pb-0 lg:pr-0 items-center lg:items-start text-center lg:text-left",
      blurb: { extraClass: "text-gray-850 font-inter text-base" },
      updatedTimeExtraClass: "text-4xs lg:text-7xs font-inter",
      updatedTimeWrapperClass: "lg:mt-auto",
    },
    title: {
      size: "text-3xl md:text-xl lg:text-5xl",
      color: "!text-green-600",
      extraClass:
        "font-bold font-fraunces leading-[24px] lg:leading-[31px] mb-2",
      spanClassName: "hover:decoration-red-210",
    },
    section: {
      extraClass: "hidden",
    },
  };

  const templateGVariant: CardVariations = {
    kicker: {
      color: "text-gray-515",
      extraClass: "hidden",
    },
    image: {
      width: "w-1/4",
      position: "left",
      extraClass: "lg:order-2",
    },
    content: {
      width: "w-3/4",
      extraClass: "h-full pl-4 lg:pl-0 lg:pr-4 lg:order-1",
      blurb: { extraClass: "font-inter text-base text-gray-850" },
      updatedTimeExtraClass: "text-4xs lg:text-7xs font-inter",
      updatedTimeWrapperClass: "pt-1 mt-auto",
    },
    title: {
      size: "text-lg lg:text-xl",
      color: "!text-green-600",
      extraClass: "font-bold mb-2 font-fraunces leading-[24px]",
      spanClassName: "hover:decoration-red-210",
    },
    section: {
      extraClass: "hidden",
    },
  };

  useInjectClientData({ gaProps });
  const breadcrumbList = getBreadcrumbProperties(entity);

  return (
    <div className="w-full" data-testid="thrive-page-component">
      <USFBreadcrumbSchema items={breadcrumbList} />

      <Ad
        adType="lb1"
        className="flex min-h-[calc(50px+0.50rem)] flex-col items-center justify-center border-b border-gray-175 bg-gray-125 py-1 text-center md:py-3 lg:min-h-[calc(250px+1.5rem)]"
      />

      <div ref={breadcrumbsRef}></div>

      <Header
        displayBreadcrumb={isShowBreadCrumb}
        parentCategory="thrive"
        childCategory="thrive"
        categoryDataMapping={sectionNavigationItems}
      />

      <PulseEntry />

      <Container>
        <ThriveTopBanner />

        <div className="mt-4 flex flex-col-reverse gap-6 lg:mt-8 lg:flex-row">
          <div className="w-full lg:w-8/12">
            <ThriveNavigation />

            <Listing
              cardProps={articles}
              urlPath={urlPath}
              pageType={"section"}
              size={10}
              sections={uniqueName}
              templateGCardVariantOverride={templateGVariant}
              templateHCardVariantOverride={templateHVariant}
              isUseTemplateH
            />
          </div>

          <div className="border-b border-green-100 lg:mb-6 lg:mt-18 lg:block lg:w-4/12 lg:border-b-0">
            <ThriveSocialMediaBanner />

            <ThriveNewsletter />

            <div className="mb-6 mt-2 hidden lg:block lg:h-[800px]">
              <Ad
                adType="imu2"
                className="flex flex-col items-center justify-center lg:!sticky lg:top-18"
              />
            </div>
          </div>
        </div>
      </Container>

      <SeoFooter data={seoFooterData} />

      <Ad adType="catfish" />
      <Ad adType="prestitial" />
    </div>
  );
};

export default ThrivePage;
